import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import ApiService from "../services/ApiService";
import PageLoader from "../components/PageLoader";
import Header from "../components/Header";
import AuthService from "../services/AuthService";

// Lists the customer's saved cards (Checkout.com instruments linked to their
// cko_customerId) and lets them remove one. The same cards are shown inside the
// Flow widget on /pay for direct payment; this page is just for managing them.
const SavedCards = () => {
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [cards, setCards] = useState([]);
  const authUser = AuthService.getCurrentUser();

  const getCards = async () => {
    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: "GET",
        url: "/getCustomerCards",
      });
      const data = response.data;

      if (data.status) {
        // Endpoint returns { data: { cards: [...] } } when a customer exists,
        // or { data: [] } when there's no customer/cards yet.
        const list = data.data?.cards || (Array.isArray(data.data) ? data.data : []);
        setCards(list);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setIsLoader(false);
    }
  };

  useEffect(() => {
    if (authUser?.is_guest) {
      toast.error("Please login or create new account to use this feature.");
      navigate("/login");
      return;
    }
    getCards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeCard = async (cardId) => {
    const result = await Swal.fire({
      title: "Remove this card?",
      text: "You'll need to re-enter it next time you pay.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#006937",
      cancelButtonColor: "#9E9B9B",
      confirmButtonText: "Yes, remove it",
    });
    if (!result.isConfirmed) return;

    try {
      setIsLoader(true);
      const response = await ApiService.request({
        method: "POST",
        url: "/deleteCard",
        data: { cardId },
      });
      const data = response.data;

      if (data.status) {
        toast.success("Card removed successfully");
        getCards();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <>
      {isLoader ? (
        <PageLoader />
      ) : (
        <div className="main-heading items-details-main container">
          <Header title={"Saved Cards"} />

          <div className="row px-3 mx-auto justify-content-center">
            {cards.map((card) => (
              <div className="col-12 col-md-6" key={card.id}>
                <div className="mb-3 p-3 border rounded bg-white d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <i className="bi bi-credit-card-2-front fs-3 me-3 text-base"></i>
                    <div>
                      <h6 className="mb-0 text-uppercase">
                        {card.scheme || "Card"} •••• {card.last4}
                      </h6>
                      <p className="text-muted item-des mb-0">
                        Expires {String(card.expiry_month).padStart(2, "0")}/{card.expiry_year}
                      </p>
                    </div>
                  </div>
                  <div
                    className="cursor-pointer"
                    style={{ cursor: "pointer" }}
                    title="Remove card"
                    onClick={() => removeCard(card.id)}
                  >
                    <i className="bi bi-trash fs-5 text-danger"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {cards.length < 1 && (
            <h6 className="my-5 text-center">
              <i className="bi bi-credit-card"></i> No saved cards found!
            </h6>
          )}
        </div>
      )}
    </>
  );
};

export default SavedCards;
